'use client';

// Client-side Meta Pixel helpers.
// The pixel script is injected ONLY after the visitor has given cookie consent
// (same gating pattern as GA4 in src/app/[lang]/layout.tsx). Before that,
// window.fbq does not exist and every helper here is a no-op.

declare global {
  interface Window {
    fbq?: (...args: any[]) => void;
    _fbq?: unknown;
  }
}

export const META_CONSENT_GRANTED_EVENT = 'meta-consent-granted';
export const META_CONSENT_REVOKED_EVENT = 'meta-consent-revoked';

let pixelInitialized = false;

export function notifyMetaConsentGranted() {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new Event(META_CONSENT_GRANTED_EVENT));
}

export function notifyMetaConsentRevoked() {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new Event(META_CONSENT_REVOKED_EVENT));
}

export function initMetaPixel(pixelId: string) {
  if (typeof window === 'undefined' || !pixelId || pixelInitialized) return;
  pixelInitialized = true;

  /* Meta Pixel base code (from the official snippet) */
  const w = window as any;
  if (w.fbq) return;
  const n: any = function () {
    // eslint-disable-next-line prefer-spread, prefer-rest-params -- verbatim Meta snippet
    n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
  };
  w.fbq = n;
  if (!w._fbq) w._fbq = n;
  n.push = n;
  n.loaded = true;
  n.version = '2.0';
  n.queue = [];
  const t = document.createElement('script');
  t.async = true;
  t.src = 'https://connect.facebook.net/en_US/fbevents.js';
  const s = document.getElementsByTagName('script')[0];
  s.parentNode?.insertBefore(t, s);

  // Disable Meta's automatic event detection (SubscribedButtonClick etc.)
  // so only the events tracked explicitly in code are sent.
  w.fbq('set', 'autoConfig', false, pixelId);
  w.fbq('init', pixelId);
  // Initial PageView for the page the visitor is on when consent is given.
  w.fbq('track', 'PageView');
}

export function revokeMetaConsent() {
  if (typeof window === 'undefined' || !window.fbq) return;
  // Stops event processing even though the script stays loaded.
  window.fbq('consent', 'revoke');
}

export function trackMetaEvent(eventName: string, params?: Record<string, any>, eventID?: string) {
  if (typeof window === 'undefined' || !window.fbq) return;
  if (eventID) {
    window.fbq('track', eventName, params ?? {}, { eventID });
  } else {
    window.fbq('track', eventName, params ?? {});
  }
}

export function trackMetaPageView() {
  trackMetaEvent('PageView');
}

export function trackMetaViewContent(payload: { contentName: string; contentType?: string }) {
  trackMetaEvent('ViewContent', {
    content_name: payload.contentName,
    content_type: payload.contentType ?? 'product',
  });
}

export type BookingContentMeta = {
  contentName?: string;
  contentIds: string[];
  numItems: number;
};

// Extracts non-personal content metadata from a booking payload (v1 cart or
// single-booking shape) shared by all booking flows.
export function buildBookingContentMeta(bookingPayload: any): BookingContentMeta {
  const items =
    bookingPayload && typeof bookingPayload === 'object'
      ? bookingPayload.version === 'v1' && Array.isArray(bookingPayload.items)
        ? bookingPayload.items
        : [bookingPayload]
      : [];

  const productTypes = Array.from(
    new Set(items.map((it: any) => String(it?.productType ?? '')).filter(Boolean))
  ) as string[];
  const contentIds = items
    .map((it: any) => String(it?.productId ?? ''))
    .filter((id: string) => Boolean(id));
  const numItems = items.reduce(
    (sum: number, it: any) => sum + (Number(it?.qty ?? it?.participantsCount ?? 1) || 1),
    0
  );

  return {
    contentName: productTypes.length ? `booking_${productTypes.join('_')}` : undefined,
    contentIds,
    numItems,
  };
}

export function trackMetaInitiateCheckout(args: {
  value?: number;
  currency?: string;
  content: BookingContentMeta;
}) {
  const params: Record<string, any> = { content_type: 'product' };
  if (args.content.contentName) params.content_name = args.content.contentName;
  if (args.content.contentIds.length) params.content_ids = args.content.contentIds;
  if (args.content.numItems > 0) params.num_items = args.content.numItems;
  if (typeof args.value === 'number' && args.value > 0) {
    params.value = args.value;
    params.currency = (args.currency || 'eur').toUpperCase();
  }
  trackMetaEvent('InitiateCheckout', params);
}

// Browser-side Purchase. `eventId` must match the server-side Conversions API
// event id so Meta deduplicates the browser/server pair.
export function trackMetaPurchase(args: {
  value: number;
  currency: string;
  eventId: string;
  contentName?: string;
  contentIds?: string[];
}) {
  const params: Record<string, any> = {
    content_type: 'product',
    value: args.value,
    currency: args.currency,
  };
  if (args.contentName) params.content_name = args.contentName;
  if (args.contentIds?.length) params.content_ids = args.contentIds;
  trackMetaEvent('Purchase', params, args.eventId);
}

// Fires InitiateCheckout when a visitor clicks a « Réserver » call-to-action
// that opens the booking flow. No-op for links that are not booking entries.
export function trackMetaBookingEntry(href: string) {
  const match = href.match(/\/booking\/(atelier|rdv|anniversaire|entreprise)/);
  if (!match) return;
  trackMetaInitiateCheckout({
    content: {
      contentName: `booking_${match[1]}`,
      contentIds: [],
      numItems: 0,
    },
  });
}

// Once-per-tab guard for the browser Purchase: allows the first send for a
// given eventId (even if the booking was created by the Stripe webhook before
// the confirmation page opened) and blocks duplicates on reload/revisit.
// Server-side, Meta deduplicates identical event_ids as a second line of defence.
export function claimMetaPurchaseEvent(eventId: string): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const key = `meta_purchase_sent_${eventId}`;
    if (window.sessionStorage.getItem(key)) return false;
    window.sessionStorage.setItem(key, '1');
    return true;
  } catch {
    return true;
  }
}
